import { BrandKit, BrandKitFormFields, BrandKitFormInputs } from './BrandKitForm.types'
import {
  BrandKitInput,
  GetTemplatesBrandKitFlowcodeDetailsQuery
} from '@dtx-company/flow-codegen/src/code/generated.types'
import { DEFAULT_FLOWCODE_IMAGE_URL } from '../../DesignHuddle/constants'
import { DebouncedState, useDebouncedCallback } from 'use-debounce'
import { GET_TEMPLATES_BRAND_KIT_FLOWCODE_DETAILS } from '@dtx-company/flow-codegen/src/code/queries'
import { ProcessedFileFormats } from '@dtx-company/design-system/src/components/FileUpload/FileUpload'
import { SubmitHandler, UseFormReturn, useFormContext } from 'react-hook-form'
import { formatBatchDataToFlowcodeType } from './BrandKitForm.utils'
import { getFlowcodeUrl } from '@dtx-company/flowcode-generator-browser/src/getFlowcodeUrl'
import { getFormattedFlowcodeOptions } from '../../../../utils/flowcode'
import { getIdFromEncodedString } from '@dtx-company/true-common/src/utils/strings'
import { gqlFetcher } from '@dtx-company/inter-app/src/services/gqlFetcher'
import {
  setBatchId,
  setBrandKit,
  setNickname,
  setSelectedFlowcode,
  setSelectedTemplateFlowcode
} from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreSlice'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useDispatch } from 'react-redux'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import { usePrintStoreBrandKitQuery } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreQueries'
import { usePrintStoreSaveBrandKitMutation } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreMutations'
import events from '@dtx-company/inter-app/src/event-tracking/events/printStore'
import useSWR from 'swr'

const ONCHANGE_DEBOUNCE_TIMEOUT = 100 // sets the debounce timeout for onChange handlers
const ONSUBMIT_DEBOUNCE_TIMEOUT = 250 // sets the debounce timeout for onSubmit function

export const useBrandKit = (): BrandKit => {
  const { brandKit } = useCodeSelector(state => state.printStore)
  return brandKit
}

export const useBrandKitLogoFilename = (): string | undefined => {
  const brandKit = useBrandKit()
  const logoImageUrl = brandKit?.logoImage
  return logoImageUrl ? logoImageUrl.split('/').pop() : undefined
}

export type BaseFieldChangedHandler<T = string> = (val: T) => void

export type UseBrandKitFormReturn = {
  onCTAChanged: DebouncedState<
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  >
  onLogoChanged: (logo: ProcessedFileFormats) => void
  onPrimaryColorChanged: DebouncedState<BaseFieldChangedHandler>
  onSecondaryColorChanged: DebouncedState<BaseFieldChangedHandler>
  formMethods: UseFormReturn<BrandKitFormInputs>
  onChangeBridge: (field: keyof BrandKitFormInputs) => BaseFieldChangedHandler
  onSubmit: SubmitHandler<BrandKitFormInputs>
  initialBrandKit?: BrandKitInput
}

export const useGetBatchDetailsForTemplates = ():
  | GetTemplatesBrandKitFlowcodeDetailsQuery
  | undefined => {
  const router = useFlowRouter()
  const routerBatchId = router.getString('batchId')
  const { token } = useAuthState()

  const { data: batchData } = useSWR(
    () =>
      token && routerBatchId ? [GET_TEMPLATES_BRAND_KIT_FLOWCODE_DETAILS, routerBatchId] : null,
    ([query, routerBatchId]) =>
      gqlFetcher<GetTemplatesBrandKitFlowcodeDetailsQuery>(query, { batchId: routerBatchId }),
    {
      revalidateOnFocus: false
    }
  )
  return batchData
}

export const useBrandKitForm = (): UseBrandKitFormReturn => {
  const [batchIdDetailsSaved, setBatchIdDetailsSaved] = useState(false)
  const formMethods = useFormContext<BrandKitFormInputs>()
  const { isAuthenticated, isAuthChecked } = useAuthState()
  const [saveBrandKit] = usePrintStoreSaveBrandKitMutation()
  const dispatch = useDispatch()

  const { data: printStoreBrandKitData, isLoading } = usePrintStoreBrandKitQuery(
    {},
    { skip: !isAuthenticated }
  )
  const batchData = useGetBatchDetailsForTemplates()

  const batchDetails = useMemo(
    () => formatBatchDataToFlowcodeType(batchData?.batch),
    [batchData?.batch]
  )

  useEffect(() => {
    if (!batchIdDetailsSaved && batchDetails) {
      const decodedBatchId = getIdFromEncodedString(batchDetails.id)
      dispatch(setSelectedFlowcode(batchDetails))
      dispatch(setSelectedTemplateFlowcode(batchDetails))
      dispatch(setBatchId(decodedBatchId))
      batchDetails.nickname && dispatch(setNickname(batchDetails.nickname))
      setBatchIdDetailsSaved(true)
    }
  }, [batchDetails, batchIdDetailsSaved, dispatch])

  const { setValue, resetField } = formMethods
  const { batchId, nickname, selectedTemplateFlowcode } = useCodeSelector(state => state.printStore)
  const formValues = formMethods.getValues() as BrandKitFormInputs
  const [prevFormValues, setPrevFormValues] = useState<BrandKitFormInputs>({
    [BrandKitFormFields.PrimaryText]: '',
    [BrandKitFormFields.PrimaryColor]: '',
    [BrandKitFormFields.SecondaryColor]: '',
    [BrandKitFormFields.LogoImage]: ''
  })

  const flowcodeImage = useMemo(() => {
    if (selectedTemplateFlowcode) {
      const options = getFormattedFlowcodeOptions(selectedTemplateFlowcode)
      return getFlowcodeUrl(options)
    } else {
      return ''
    }
  }, [selectedTemplateFlowcode])

  /**
   * A bridge function to connect the `react-hook-form` onChange handler
   * with the onChange values passed from our custom input components.
   * Triggers field validation.
   *
   * @example
   * <ColorPicker onChange={(val) => onChangeBridge('primaryColor')(val)} />
   */
  const onChangeBridge: (field: keyof BrandKitFormInputs) => (val: string | undefined) => void =
    useCallback(
      field => val => {
        if (val) setValue(field, val, { shouldValidate: true })
        else resetField(field)
      },
      [setValue, resetField]
    )

  const initialBrandKit = useMemo<BrandKit | undefined>(() => {
    if (isAuthenticated && !isLoading) {
      const { primaryText, primaryColor, secondaryColor, logoImage } =
        printStoreBrandKitData?.getBrandKit || {}
      return {
        primaryText,
        primaryColor,
        secondaryColor,
        logoImage,
        templatesListFlowcodeImage: flowcodeImage || DEFAULT_FLOWCODE_IMAGE_URL,
        editorFlowcodeImage: flowcodeImage || DEFAULT_FLOWCODE_IMAGE_URL,
        highlightedTemplatesFlowcodeImage: DEFAULT_FLOWCODE_IMAGE_URL
      }
    } else {
      return {
        templatesListFlowcodeImage: '',
        editorFlowcodeImage: '',
        highlightedTemplatesFlowcodeImage: '',
        [BrandKitFormFields.PrimaryText]: '',
        [BrandKitFormFields.PrimaryColor]: '',
        [BrandKitFormFields.SecondaryColor]: '',
        [BrandKitFormFields.LogoImage]: ''
      }
    }
  }, [flowcodeImage, isAuthenticated, isLoading, printStoreBrandKitData?.getBrandKit])

  useEffect(() => {
    if (initialBrandKit) {
      onChangeBridge(BrandKitFormFields.PrimaryText)(initialBrandKit.primaryText)
      onChangeBridge(BrandKitFormFields.LogoImage)(initialBrandKit.logoImage)
      onChangeBridge(BrandKitFormFields.PrimaryColor)(initialBrandKit.primaryColor)
      onChangeBridge(BrandKitFormFields.SecondaryColor)(initialBrandKit.secondaryColor)
      dispatch(setBrandKit(initialBrandKit))
    }
  }, [batchId, dispatch, initialBrandKit, nickname, onChangeBridge])

  const buildBrandKitInputFromFormData = useCallback(
    async (
      data: BrandKitFormInputs
    ): Promise<{
      brandKit: BrandKit
      brandKitInput: BrandKitInput
    }> => {
      const { primaryText, primaryColor, secondaryColor, logoImage } = data

      const brandKit: BrandKit = {
        templatesListFlowcodeImage: flowcodeImage,
        editorFlowcodeImage: flowcodeImage,
        highlightedTemplatesFlowcodeImage: '',
        logoImage,
        primaryText,
        primaryColor,
        secondaryColor
      }

      const brandKitInput: BrandKitInput = {
        ...(primaryText && { primaryText }),
        ...(primaryColor && { primaryColor }),
        ...(secondaryColor && { secondaryColor }),
        ...(logoImage && { logoImage })
      }

      return {
        brandKit,
        brandKitInput
      }
    },
    [flowcodeImage]
  )

  /**
   * The `onSubmit` handler that is called when the user submits the Brand Kit form.
   *
   * Stores the form values in redux and POST to the backend.
   */
  const _onSubmit: SubmitHandler<BrandKitFormInputs> = useCallback(
    async (data: BrandKitFormInputs) => {
      try {
        events.PrintTemplates_Templates_Brand_Kit_Clicked_Update()

        const { brandKitInput, brandKit } = await buildBrandKitInputFromFormData(data)

        dispatch(setBrandKit(brandKit))
        if (isAuthenticated && isAuthChecked) {
          saveBrandKit({ brandKit: brandKitInput })
        }
      } catch (e) {
        console.error('Error submitting brand kit form.', e, data)
      }
    },
    [buildBrandKitInputFromFormData, isAuthenticated, isAuthChecked, dispatch, saveBrandKit]
  )

  // Necessary to debounce the onSubmit handler to batch multiple brandkit changes
  // in a short period of time and avoid unnecessary api requests
  const onSubmit: SubmitHandler<BrandKitFormInputs> = useDebouncedCallback(
    _onSubmit,
    ONSUBMIT_DEBOUNCE_TIMEOUT
  )

  useEffect(() => {
    const hasChanged = Object.keys(formValues).reduce((acc, key) => {
      const formKey = key as BrandKitFormFields
      return acc || formValues?.[formKey] !== prevFormValues?.[formKey]
    }, false)

    if (hasChanged) {
      setPrevFormValues(() => formValues)
      onSubmit(formValues)
    }
  }, [onSubmit, formValues, prevFormValues, formMethods])

  /**
   * The `onChange` handler for the {@link FileUpload} component.
   *
   * Tracks a Heap event when the user uploads a logo.
   */
  const onLogoChanged = (logo?: ProcessedFileFormats): void => {
    return logo
      ? events.PrintTemplates_Templates_Brand_Kit_Added_Logo()
      : events.PrintTemplates_Templates_Brand_Kit_Removed_Logo()
  }

  /**
   * The `onChange` handler for the CTA input.
   *
   * Tracks a Heap event when the user enters a CTA using a debounced callback so it does not fire on every keystroke.
   * Sets the value of the `cta` field in the form via {@link onChangeBridge}.
   *
   * @note
   * This function, unlike other `onChange` handlers in this file, accepts a {@link React.ChangeEvent}
   * since that's what our {@link TextInput} component emits in its `onChange` handler,
   * while some of our more customized  input components like {@link ColorPicker} emit a string.
   */
  const onCTAChanged = useDebouncedCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const cta = event.target.value
      onChangeBridge(BrandKitFormFields.PrimaryText)(cta)
      events.PrintTemplates_Templates_Brand_Kit_Entered_CTA({ cta })
    },
    ONCHANGE_DEBOUNCE_TIMEOUT
  )

  /**
   * The `onChange` handler for the primary color input.
   *
   * Tracks a Heap event when the user selects a primary color using a debounced callback
   * to prevent multiple events from being tracked, as the user is likely to be dragging the color picker.
   * Sets the value of the `primaryColor` field in the form via {@link onChangeBridge}.
   */
  const onPrimaryColorChanged = useDebouncedCallback((color: string) => {
    onChangeBridge(BrandKitFormFields.PrimaryColor)(color)
    events.PrintTemplates_Templates_Brand_Kit_Selected_Primary_Color({ color })
  }, ONCHANGE_DEBOUNCE_TIMEOUT)

  /**
   * The `onChange` handler for the secondary color input.
   *
   * Tracks a Heap event when the user selects a secondary color using a debounced callback
   * to prevent multiple events from being tracked, as the user is likely to be dragging the color picker.
   * Sets the value of the `secondaryColor` field in the form via {@link onChangeBridge}.
   */
  const onSecondaryColorChanged = useDebouncedCallback((color: string) => {
    onChangeBridge(BrandKitFormFields.SecondaryColor)(color)
    events.PrintTemplates_Templates_Brand_Kit_Selected_Secondary_Color({ color })
  }, ONCHANGE_DEBOUNCE_TIMEOUT)

  return {
    onCTAChanged,
    onLogoChanged,
    onPrimaryColorChanged,
    onSecondaryColorChanged,
    formMethods,
    onChangeBridge,
    onSubmit,
    initialBrandKit
  }
}
